import { useState } from 'react';
import { toast } from 'sonner';
import { useBlocker } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { FeatureFlags } from '@condo/contracts';
import { useFeatureFlags, usePatchFeatureFlags } from './api.js';
import { useFlagForm } from './use-flag-form.js';
import { FlagSection } from './flag-section.js';
import { PreSaveSummary } from './pre-save-summary.js';

interface Props {
  condoId: string;
}

// ─── Outer shell ────────────────────────────────────────────────────────────
// Handles data-fetching lifecycle. Only renders the loaded inner component
// once data is available, keeping all hook calls unconditional.

export function FeatureFlagsTab({ condoId }: Props) {
  const { data, isLoading, isError } = useFeatureFlags(condoId);

  if (isLoading || !data) {
    return (
      <div className="space-y-4 py-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="py-8 text-sm text-[var(--color-ink-muted)]">
        No se pudieron cargar los feature flags. Reintentar recargando la pagina.
      </p>
    );
  }

  return <FeatureFlagsTabLoaded condoId={condoId} data={data} />;
}

// ─── Inner loaded component ──────────────────────────────────────────────────
// All hooks here run unconditionally — data is guaranteed non-null.

interface LoadedProps {
  condoId: string;
  data: FeatureFlags;
}

function FeatureFlagsTabLoaded({ condoId, data }: LoadedProps) {
  const form = useFlagForm(data);
  const patch = usePatchFeatureFlags(condoId);
  const [modalOpen, setModalOpen] = useState(false);

  // Prompt on route change if the form is dirty.
  useBlocker(({ currentLocation, nextLocation }) => {
    if (!form.isDirty) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return !window.confirm(
      `Tienes ${form.diff.length} cambio(s) sin guardar. Salir de todos modos?`,
    );
  });

  const handleDiscard = () => {
    if (!form.isDirty) return;
    if (window.confirm(`Descartar los ${form.diff.length} cambio(s) pendientes?`)) {
      form.reset();
    }
  };

  const handleSave = () => setModalOpen(true);

  const handleConfirm = () => {
    patch.mutate(form.buildPatch(), {
      onSuccess: () => {
        toast.success('Feature flags actualizados');
        form.reset();
        setModalOpen(false);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Error desconocido';
        toast.error('No se pudieron guardar los cambios', { description: msg });
      },
    });
  };

  return (
    <div className="relative pb-20">
      <FlagSection group="modules" form={form} />
      <FlagSection group="ia" form={form} />
      <FlagSection group="integrations" form={form} />
      <FlagSection group="red" form={form} />

      <div className="sticky bottom-0 -mx-4 -mb-4 mt-8 flex items-center justify-between border-t border-[var(--color-hairline)] bg-[var(--color-paper)] px-4 py-3">
        <p className="text-xs text-[var(--color-ink-muted)]">
          {form.isDirty
            ? `${form.diff.length} cambio${form.diff.length === 1 ? '' : 's'} pendiente${form.diff.length === 1 ? '' : 's'}`
            : 'Sin cambios pendientes'}
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" disabled={!form.isDirty} onClick={handleDiscard}>
            Descartar
          </Button>
          <Button disabled={!form.isDirty} onClick={handleSave}>
            Guardar cambios
          </Button>
        </div>
      </div>

      <PreSaveSummary
        open={modalOpen}
        diff={form.diff}
        loading={patch.isPending}
        onConfirm={handleConfirm}
        onCancel={() => !patch.isPending && setModalOpen(false)}
      />
    </div>
  );
}
